import { useState } from 'react';
import { apiPost } from '../lib/apiClient';

interface QualityScore {
  overall: number;
  dimensions: {
    adJargon: number;
    readability: number;
    attractiveness: number;
    personaMatch: number;
    creativity: number;
  };
  details: {
    clichesFound: string[];
    emotionalScore: number;
    actionScore: number;
    sentenceComplexity: number;
    keywordMatch: number;
  };
  suggestions: string[];
}

interface Headline {
  id: string;
  type: 'H1' | 'H2' | 'H3';
  text: string;
  charCount: number;
  keyword?: string;
  usp?: string;
  cta?: string;
  isCompliant: boolean;
  qualityScore?: QualityScore;
}

interface Description {
  id: string;
  variant: string;
  text: string;
  charCount: number;
  keyword: string;
  isCompliant: boolean;
  issues?: string[];
  qualityScore?: QualityScore;
}

interface GeneratedAssets {
  headlines: Headline[];
  descriptions: Description[];
}

interface BatchTask {
  id: string;
  keywords: string[];
  persona: string;
  scene?: string;
  status: string;
  headlineCount: number;
  descriptionCount: number;
  avgQualityScore: number;
}

interface Persona {
  id: string;
  name: string;
  description: string;
  bestScenes: string[];
}

const PERSONAS: Persona[] = [
  { id: 'postpartum', name: '產後媽媽', description: '產後修復、恢復身材目標', bestScenes: ['辦公室工作', '節慶活動', '居家休閒', '產後照護', '工作外勤'] },
  { id: 'weight_loss', name: '減肥瘦身', description: '減重、瘦身、塑形目標', bestScenes: ['辦公室工作', '居家休閒', '節慶活動', '外出活動', '社交聚會'] },
  { id: 'back_pain', name: '腰痠背痛', description: '久坐、駝背、腰背痠痛改善目標', bestScenes: ['居家辦公', '辦公室工作', '居家休閒', '工作外勤', '社交聚會'] },
  { id: 'heat_sensitive', name: '怕熱女孩', description: '怕悶熱、擔心穿不住塑身衣', bestScenes: ['辦公室工作', '居家休閒', '外出活動', '工作外勤', '節慶活動'] },
  { id: 'premium', name: '名媛貴婦', description: '重視品味、形象、氣質的女性', bestScenes: ['運動健身', '工作外勤', '社交聚會', '節慶活動', '外出活動'] }
];

// 所有可能的場景選項（合併所有 Persona 的場景）
const ALL_SCENES = [
  '辦公室工作',
  '節慶活動',
  '居家休閒',
  '產後照護',
  '工作外勤',
  '外出活動',
  '社交聚會',
  '居家辦公',
  '運動健身'
];

interface BatchResult {
  success: boolean;
  summary: {
    total: number;
    success: number;
    failed: number;
  };
  exports: {
    csv: string;
    googleAdsTSV: string;
  };
  results: BatchTask[];
}

export function BatchCreator() {
  const [tasks, setTasks] = useState<Array<{
    id: number;
    keywords: string;
    persona: string;
    scene: string;
  }>>([{ id: 1, keywords: '', persona: '', scene: '' }]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState('');

  function addTask() {
    setTasks([...tasks, { id: Date.now(), keywords: '', persona: '', scene: '' }]);
  }

  function removeTask(id: number) {
    setTasks(tasks.filter(t => t.id !== id));
  }

  function updateTask(id: number, field: string, value: string) {
    setTasks(tasks.map(t => t.id === id ? { ...t, [field]: value } : t));
  }

  async function generateBatch() {
    if (tasks.some(t => !t.keywords.trim() || !t.persona)) {
      setError('請確保所有任務都有關鍵字和 Persona');
      return;
    }

    setLoading(true);
    setError('');
    setProgress({ current: 0, total: tasks.length });

    try {
      const requestBody = {
        tasks: tasks.map(t => ({
          keywords: t.keywords.split(',').map(k => k.trim()).filter(Boolean),
          persona: t.persona,
          scene: t.scene || undefined
        }))
      };

      const response = await apiPost('/api/ad-creator/batch-generate', requestBody);

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setProgress({ current: tasks.length, total: tasks.length });
      } else {
        setError(data.error || '批量生成失敗');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '發生錯誤');
    } finally {
      setLoading(false);
    }
  }

  function downloadCSV() {
    if (!result?.exports?.csv) return;
    const blob = new Blob([result.exports.csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `batch_copy_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  function downloadTSV() {
    if (!result?.exports?.googleAdsTSV) return;
    const blob = new Blob([result.exports.googleAdsTSV], { type: 'text/tab-separated-values;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `google_ads_bulk_${new Date().toISOString().split('T')[0]}.tsv`;
    link.click();
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          📦 批量文案生成器
        </h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          一次生成多組關鍵字的廣告文案，支援 CSV 匯出
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          background: 'var(--color-error-bg)',
          color: 'var(--color-error)',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Task List */}
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: '0.75rem',
        padding: '2rem',
        marginBottom: '1.5rem'
      }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
          生成任務列表
        </h2>

        {tasks.map((task, index) => (
          <div
            key={task.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr auto',
              gap: '1rem',
              alignItems: 'center',
              padding: '1rem',
              background: 'var(--color-bg)',
              borderRadius: '0.5rem',
              marginBottom: '0.75rem'
            }}
          >
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                關鍵字（用逗號分隔）
              </label>
              <input
                type="text"
                value={task.keywords}
                onChange={(e) => updateTask(task.id, 'keywords', e.target.value)}
                placeholder="塑身衣, 產後塑身, 瘦身衣"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                Persona
              </label>
              <select
                value={task.persona}
                onChange={(e) => updateTask(task.id, 'persona', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">選擇 Persona</option>
                {PERSONAS.map((p: Persona) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                場景（選填）
              </label>
              <select
                value={task.scene}
                onChange={(e) => updateTask(task.id, 'scene', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--color-border)',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">選擇場景（或不選）</option>
                {ALL_SCENES.map(scene => (
                  <option key={scene} value={scene}>{scene}</option>
                ))}
              </select>
            </div>

            <button
              onClick={() => removeTask(task.id)}
              disabled={tasks.length === 1}
              style={{
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                cursor: tasks.length === 1 ? 'not-allowed' : 'pointer',
                color: tasks.length === 1 ? 'var(--color-text-tertiary)' : 'var(--color-error)',
                fontSize: '1.25rem'
              }}
            >
              🗑️
            </button>
          </div>
        ))}

        <button
          onClick={addTask}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: '2px dashed var(--color-border)',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            width: '100%'
          }}
        >
          ＋ 新增任務
        </button>
      </div>

      {/* Generate Button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
        <button
          onClick={generateBatch}
          disabled={loading}
          style={{
            padding: '1rem 3rem',
            background: loading ? 'var(--color-border)' : 'var(--color-primary)',
            color: 'white',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {loading ? `生成中... ${progress.current}/${progress.total}` : '🚀 開始批量生成'}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: '0.75rem',
          padding: '2rem'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
            ✅ 生成完成
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-primary)' }}>{result.summary.total}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>總任務數</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-success)' }}>{result.summary.success}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>成功</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-error)' }}>{result.summary.failed}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>失敗</div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-warning)' }}>
                {Math.round(result.results.reduce((acc, r) => acc + r.avgQualityScore, 0) / result.results.length)}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>平均品質分</div>
            </div>
          </div>

          {/* Task Results Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem' }}>關鍵字</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem' }}>Persona</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem' }}>Headlines</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem' }}>Descriptions</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem' }}>品質分</th>
                <th style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.875rem' }}>狀態</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{r.keywords.join(', ')}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{r.persona}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>{r.headlineCount}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>{r.descriptionCount}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>{r.avgQualityScore}</td>
                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>
                    {r.status === 'COMPLETED' ? '✅' : '❌'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Download Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={downloadCSV}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              📥 下載 CSV
            </button>
            <button
              onClick={downloadTSV}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-success)',
                color: 'white',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              📥 下載 Google Ads TSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
